/**
 * BobbyProductGallery
 *
 * MOBILE: scroll nativo OS con inerzia fisica, scroll-snap CSS sempre attivo.
 * DESKTOP: mousedown disabilita snap (.bobby-dragging) → drag 1:1 libero →
 *   mouseup su window applica momentum RAF + decay → _snapToNearest scrollTo smooth →
 *   scrollend (o timeout fallback) rimuove .bobby-dragging e riattiva snap CSS.
 */
class BobbyProductGallery {

    static SCROLL_DEBOUNCE = 80;
    static RESIZE_DEBOUNCE = 250;
    static MOMENTUM_DECAY = 0.90;
    static MOMENTUM_MIN = 0.8;

    constructor(container) {
        this.container = container;
        this.gallery = container.querySelector('.bobby-gallery-container');
        this.slides = container.querySelectorAll('.bobby-gallery-slide');
        this.dots = container.querySelectorAll('.bobby-dot');
        this.thumbs = container.querySelectorAll('.bobby-thumb');
        this.currentSlide = 0;
        this.scrollTimer = null;
        this.resizeTimer = null;
        this._momentumRaf = null;

        this.init();
    }

    init() {
        if (this.slides.length <= 1) return;

        this.bindDots();
        this.bindThumbs();
        this.bindDragDesktop();
        this.bindScroll();
        this.bindKeyboard();
        this.bindResize();
        this.handleLazyLoading();
        this.initIntersectionObserver();
        this.checkInitialVariant();
        this.listenVariantChange();
    }

    bindDots() {
        this.dots.forEach((dot, i) => {
            dot.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToSlide(i);
            });
        });
    }

    bindThumbs() {
        this.thumbs.forEach((thumb, i) => {
            thumb.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToSlide(i);
            });
        });
    }

    bindDragDesktop() {
        let isDragging = false;
        let startX = 0;
        let scrollStart = 0;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;

        const onMouseDown = (e) => {
            this._stopMomentum();
            isDragging = true;
            startX = e.pageX;
            lastX = e.pageX;
            lastTime = performance.now();
            scrollStart = this.gallery.scrollLeft;
            velocity = 0;
            this.gallery.classList.add('bobby-dragging');
            e.preventDefault();
        };

        // mousemove su window: il drag non si rompe se il cursore esce dalla gallery
        const onMouseMove = (e) => {
            if (!isDragging) return;
            const now = performance.now();
            const dt = now - lastTime;
            if (dt > 0) velocity = (e.pageX - lastX) / dt;
            this.gallery.scrollLeft = scrollStart - (e.pageX - startX);
            lastX = e.pageX;
            lastTime = now;
        };

        // mouseup su window: rilascio sempre catturato anche fuori dalla gallery
        const onMouseUp = () => {
            if (!isDragging) return;
            isDragging = false;
            this._applyMomentum(velocity);
        };

        this.gallery.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        // touchstart ferma solo il momentum desktop eventualmente in corso
        this.gallery.addEventListener('touchstart', () => this._stopMomentum(), {passive: true});
    }

    _applyMomentum(velocity) {
        let speed = velocity * 16; // px/ms → px/frame @60fps

        const step = () => {
            if (Math.abs(speed) < BobbyProductGallery.MOMENTUM_MIN) {
                this._snapToNearest();
                return;
            }
            this.gallery.scrollLeft -= speed;
            speed *= BobbyProductGallery.MOMENTUM_DECAY;
            this._momentumRaf = requestAnimationFrame(step);
        };

        this._momentumRaf = requestAnimationFrame(step);
    }

    _snapToNearest() {
        // AbortController: annulla listener scrollend stale del drag precedente
        if (this._snapAbortController) this._snapAbortController.abort();
        this._snapAbortController = new AbortController();

        const slideWidth = (this.slides[0]?.offsetWidth ?? this.gallery.offsetWidth) + 5;
        const clamped = Math.max(0, Math.min(Math.round(this.gallery.scrollLeft / slideWidth), this.slides.length - 1));

        this.gallery.scrollTo({left: clamped * slideWidth, behavior: 'smooth'});

        const removeDragging = () => this.gallery.classList.remove('bobby-dragging');

        this.gallery.addEventListener('scrollend', removeDragging, {
            once: true,
            signal: this._snapAbortController.signal,
        });

        // Fallback Safari < 16.4 (no scrollend)
        this._snapFallbackTimer = setTimeout(removeDragging, 400);

        if (clamped !== this.currentSlide) {
            this.currentSlide = clamped;
            this.updateNavigation();
            this.loadSlideMedia(this.currentSlide);
            this.announceSlideChange();
        }
    }

    _stopMomentum() {
        if (this._momentumRaf) {
            cancelAnimationFrame(this._momentumRaf);
            this._momentumRaf = null;
        }
        if (this._snapAbortController) {
            this._snapAbortController.abort();
            this._snapAbortController = null;
        }
        clearTimeout(this._snapFallbackTimer);
        this.gallery.classList.remove('bobby-dragging');
    }

    bindScroll() {
        this.gallery.addEventListener('scroll', () => {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(() => {
                const slideWidth = this.slides[0]?.offsetWidth || this.gallery.offsetWidth;
                const clamped = Math.max(0, Math.min(Math.round(this.gallery.scrollLeft / (slideWidth + 5)), this.slides.length - 1));
                if (clamped !== this.currentSlide) {
                    this.currentSlide = clamped;
                    this.updateNavigation();
                    this.loadSlideMedia(this.currentSlide);
                    this.announceSlideChange();
                }
            }, BobbyProductGallery.SCROLL_DEBOUNCE);
        }, {passive: true});
    }

    bindKeyboard() {
        this.container.addEventListener('keydown', (e) => {
            const onNav = e.target.classList.contains('bobby-dot') || e.target.classList.contains('bobby-thumb');
            if (!onNav) return;

            const map = {
                ArrowLeft: this.currentSlide - 1,
                ArrowRight: this.currentSlide + 1,
                Home: 0,
                End: this.slides.length - 1,
            };

            if (map[e.key] !== undefined) {
                e.preventDefault();
                this.goToSlide(map[e.key]);
            }
        });
    }

    bindResize() {
        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                const slideWidth = this.slides[0]?.offsetWidth || this.gallery.offsetWidth;
                this.gallery.scrollLeft = (slideWidth + 5) * this.currentSlide;
            }, BobbyProductGallery.RESIZE_DEBOUNCE);
        });
    }

    goToSlide(index, smooth = true) {
        if (index < 0 || index >= this.slides.length || index === this.currentSlide) return;
        this._stopMomentum();
        this.currentSlide = index;
        this.slides[index].scrollIntoView({behavior: smooth ? 'smooth' : 'instant', block: 'nearest', inline: 'start'});
        this.updateNavigation();
        this.loadSlideMedia(index);
        this.announceSlideChange();
    }

    updateNavigation() {
        this.dots.forEach((dot, i) => {
            dot.classList.toggle('bobby-active', i === this.currentSlide);
            dot.setAttribute('aria-pressed', String(i === this.currentSlide));
        });
        this.thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('bobby-active', i === this.currentSlide);
            thumb.setAttribute('aria-pressed', String(i === this.currentSlide));
        });
    }

    loadSlideMedia(index) {
        const next = this.slides[index + 1];
        if (next) {
            const img = next.querySelector('img[loading="lazy"]');
            if (img && !img.complete) img.loading = 'eager';
        }
    }

    handleLazyLoading() {
        this.slides.forEach(slide => {
            const img = slide.querySelector('img');
            if (img?.complete) img.classList.add('bobby-loaded');
        });
    }

    initIntersectionObserver() {
        if (!('IntersectionObserver' in window)) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.target.loading === 'lazy') entry.target.loading = 'eager';
            });
        }, {rootMargin: '50px'});

        this.slides.forEach(slide => {
            const img = slide.querySelector('img[loading="lazy"]');
            if (img) observer.observe(img);
        });
    }

    announceSlideChange() {
        let region = this.container.querySelector('.bobby-sr-live-region');
        if (!region) {
            region = document.createElement('div');
            region.className = 'bobby-sr-live-region bobby-sr-only';
            region.setAttribute('aria-live', 'polite');
            region.setAttribute('aria-atomic', 'true');
            this.container.appendChild(region);
        }

        const slide = this.slides[this.currentSlide];
        const type = slide.querySelector('model-viewer') ? '3D Model'
            : slide.querySelector('video, iframe') ? 'Video'
                : 'Image';

        region.textContent = `${type} ${this.currentSlide + 1} of ${this.slides.length}`;
    }

    checkInitialVariant() {
        const sel = document.querySelector('[name="id"]');
        if (sel?.value) setTimeout(() => this.switchToVariant(sel.value), 100);
    }

    listenVariantChange() {
        document.addEventListener('variant:change', (e) => {
            if (e.detail?.variant) this.switchToVariant(e.detail.variant.id.toString());
        });

        const sel = document.querySelector('[name="id"]');
        if (sel) sel.addEventListener('change', (e) => this.switchToVariant(e.target.value));
    }

    switchToVariant(variantId) {
        const slideIndex = Array.from(this.slides).findIndex(s => s.dataset.bobbyVariantId === variantId);
        if (slideIndex !== -1) this.goToSlide(slideIndex);
    }

    next() {
        this.goToSlide(this.currentSlide + 1);
    }

    prev() {
        this.goToSlide(this.currentSlide - 1);
    }

    getCurrentSlide() {
        return this.currentSlide;
    }

    getTotalSlides() {
        return this.slides.length;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.bobby-product-gallery:not([data-bobby-initialized])').forEach(el => {
        new BobbyProductGallery(el);
        el.setAttribute('data-bobby-initialized', 'true');
    });
});

if (typeof module !== 'undefined' && module.exports) module.exports = BobbyProductGallery;